(async () => {
  "use strict";

  const fallbackCatalog = window.EYE_CANDY_PICKER_CATALOG;
  const collectionConfig = window.EYE_CANDY_COLLECTION_CONFIG || {};
  const inlineCollections = window.EYE_CANDY_PICKER_INLINE_COLLECTIONS;
  const STORAGE_KEY = "eyeCandyCompareHandles";
  const MAX_SELECTED = 30;

  function normalizeCollection(collection) {
    const handle = String(collection.handle || collection.Handle || "").trim();
    return {
      handle,
      label: String(collection.label || collection.Label || handle).trim()
    };
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) return tags;
    if (typeof tags === "string") return tags.split(",").map((tag) => tag.trim()).filter(Boolean);
    return [];
  }

  function normalizeImages(product) {
    const sourceImages = Array.isArray(product.images) && product.images.length
      ? product.images
      : product.image
        ? [{ src: product.image, alt: product.imageAlt || product.title }]
        : [];

    return sourceImages
      .map((image) => ({
        src: String(image.src || image.url || ""),
        alt: String(image.alt || product.imageAlt || product.title || "Product image")
      }))
      .filter((image) => image.src);
  }

  function normalizeVariants(product) {
    return (product.variants || []).map((variant) => ({
      id: String(variant.id),
      title: String(variant.title || "Default Title"),
      price: Number(variant.price) || 0,
      available: Boolean(variant.available)
    }));
  }

  function readStoredHandles() {
    try {
      const handles = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(handles) ? handles.filter((handle) => typeof handle === "string") : [];
    } catch {
      return [];
    }
  }

  function writeStoredHandles(products) {
    const handles = products.map((product) => product.handle).slice(0, MAX_SELECTED);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
      window.dispatchEvent(new CustomEvent("eye-candy:compare-updated", { detail: { handles } }));
    } catch {
      // Browsing still works when storage is unavailable.
    }
  }

  function loadInlineCollections(collections) {
    if (!Array.isArray(collections) || !collections.length) return null;

    const productsById = new Map();
    const catalogCollections = [];

    collections.forEach((collection, collectionIndex) => {
      const normalizedCollection = normalizeCollection(collection);
      if (!normalizedCollection.handle) return;
      catalogCollections.push({ Handle: normalizedCollection.handle, Label: normalizedCollection.label });

      (collection.products || []).forEach((product, productIndex) => {
        const images = normalizeImages(product);
        if (!images.length) return;

        const id = String(product.id);
        let record = productsById.get(id);
        if (!record) {
          record = {
            id,
            title: product.title,
            handle: product.handle,
            url: product.url || `/products/${product.handle}`,
            image: images[0].src,
            imageAlt: images[0].alt,
            images,
            description: String(product.description || "").trim(),
            variants: normalizeVariants(product),
            price: Number(product.price) || 0,
            available: Boolean(product.available),
            rank: (collectionIndex * 1000) + productIndex,
            collectionHandles: [],
            collectionLabels: [],
            tags: normalizeTags(product.tags)
          };
          productsById.set(id, record);
        }

        if (!record.collectionHandles.includes(normalizedCollection.handle)) {
          record.collectionHandles.push(normalizedCollection.handle);
          record.collectionLabels.push(normalizedCollection.label);
        }
        record.rank = Math.min(record.rank, (collectionIndex * 1000) + productIndex);
      });
    });

    if (!productsById.size) return null;
    return {
      generatedAt: new Date().toISOString(),
      source: "shopify-liquid-collections",
      collections: catalogCollections,
      products: [...productsById.values()]
    };
  }

  async function loadShopifyCollections(config) {
    const collections = (config.collections || []).map(normalizeCollection).filter((collection) => collection.handle);
    if (!collections.length) throw new Error("No Shopify collections configured");

    const routeRoot = window.Shopify?.routes?.root || "/";
    const isLocalFile = window.location.protocol === "file:";
    const storeOrigin = String(config.storeOrigin || "").replace(/\/$/, "");
    const productsById = new Map();
    const failedCollections = [];

    await Promise.all(collections.map(async (collection, collectionIndex) => {
      const relativePath = `${routeRoot}collections/${encodeURIComponent(collection.handle)}/products.json?limit=250`;
      const url = isLocalFile ? `${storeOrigin}${relativePath}` : relativePath;

      try {
        const response = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-cache" });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const feed = await response.json();

        (feed.products || []).forEach((product, productIndex) => {
          const images = normalizeImages(product);
          if (!images.length) return;

          const id = String(product.id);
          let record = productsById.get(id);
          if (!record) {
            const prices = (product.variants || []).map((variant) => Number(variant.price)).filter(Number.isFinite);
            record = {
              id,
              title: product.title,
              handle: product.handle,
              url: `${isLocalFile ? storeOrigin : routeRoot.replace(/\/$/, "")}/products/${product.handle}`,
              image: images[0].src,
              imageAlt: images[0].alt,
              images,
              description: "",
              variants: normalizeVariants(product),
              price: prices.length ? Math.min(...prices) : 0,
              available: (product.variants || []).some((variant) => variant.available),
              rank: (collectionIndex * 1000) + productIndex,
              collectionHandles: [],
              collectionLabels: [],
              tags: normalizeTags(product.tags)
            };
            productsById.set(id, record);
          }

          if (!record.collectionHandles.includes(collection.handle)) {
            record.collectionHandles.push(collection.handle);
            record.collectionLabels.push(collection.label);
          }
          record.rank = Math.min(record.rank, (collectionIndex * 1000) + productIndex);
        });
      } catch (error) {
        failedCollections.push(collection.label);
      }
    }));

    if (!productsById.size) throw new Error("Shopify collection feeds could not be loaded");
    return {
      catalog: {
        generatedAt: new Date().toISOString(),
        source: "live-shopify-collections",
        collections: collections.map((collection) => ({ Handle: collection.handle, Label: collection.label })),
        products: [...productsById.values()]
      },
      failedCollections
    };
  }

  let catalog = loadInlineCollections(inlineCollections);
  let catalogMode = catalog ? "inline" : "live";
  let failedCollections = [];

  if (!catalog) {
    try {
      const loaded = await loadShopifyCollections(collectionConfig);
      catalog = loaded.catalog;
      failedCollections = loaded.failedCollections;
    } catch (error) {
      catalog = fallbackCatalog;
      catalogMode = "fallback";
    }
  }

  if (!catalog || !Array.isArray(catalog.products)) {
    const pickerShell = document.querySelector(".ec-picker-shell");
    const target = pickerShell || document.querySelector("main") || document.body;
    target.innerHTML = '<div class="picker-empty"><h2>Select a Shopify collection</h2><p>Open this section in the Shopify Theme Editor and choose at least one collection.</p></div>';
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const requestedHandles = [...new Set([
    ...(params.get("compare") || "").split(",").filter(Boolean),
    ...readStoredHandles()
  ])].slice(0, MAX_SELECTED);

  async function hydrateMissingProduct(handle) {
    const routeRoot = window.Shopify?.routes?.root || "/";
    try {
      const response = await fetch(`${routeRoot}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: "application/json" }
      });
      if (!response.ok) return null;
      const product = await response.json();
      const images = (product.images || []).map((image) => ({
        src: typeof image === "string" ? image : image.src,
        alt: product.title
      })).filter((image) => image.src);
      if (!images.length && product.featured_image) images.push({ src: product.featured_image, alt: product.title });
      if (!images.length) return null;

      return {
        id: String(product.id),
        title: product.title,
        handle: product.handle,
        url: `${routeRoot}products/${product.handle}`,
        image: images[0].src,
        imageAlt: images[0].alt,
        images,
        description: "",
        variants: (product.variants || []).map((variant) => ({
          id: String(variant.id),
          title: variant.title || "Default Title",
          price: (Number(variant.price) || 0) / 100,
          available: Boolean(variant.available)
        })),
        price: (Number(product.price) || 0) / 100,
        available: Boolean(product.available),
        rank: catalog.products.length + 10000,
        collectionHandles: [],
        collectionLabels: ["Selected from collection"],
        tags: []
      };
    } catch {
      return null;
    }
  }

  const knownHandles = new Set(catalog.products.map((product) => product.handle));
  const hydratedProducts = await Promise.all(
    requestedHandles.filter((handle) => !knownHandles.has(handle)).map(hydrateMissingProduct)
  );
  catalog.products.push(...hydratedProducts.filter(Boolean));

  const productsByHandle = new Map(catalog.products.map((product) => [product.handle, product]));
  const initialSelection = requestedHandles
    .map((handle) => productsByHandle.get(handle))
    .filter(Boolean)
    .slice(0, MAX_SELECTED);

  const state = {
    query: params.get("q") || "",
    collection: params.get("collection") || "all",
    sort: params.get("sort") || "featured",
    selected: initialSelection,
    limit: 36,
    maxSelected: MAX_SELECTED
  };

  const elements = {
    search: document.querySelector("#search-input"),
    sort: document.querySelector("#sort-select"),
    clearFilters: document.querySelector("#clear-filters"),
    filters: document.querySelector("#filter-list"),
    count: document.querySelector("#results-count"),
    catalogStatus: document.querySelector("#catalog-status"),
    grid: document.querySelector("#product-grid"),
    loadMore: document.querySelector("#load-more"),
    comparison: document.querySelector("#comparison"),
    clearComparison: document.querySelector("#clear-comparison"),
    compareNow: document.querySelector("#compare-now"),
    selectionCount: document.querySelector("#selection-count"),
    selectionSlots: document.querySelector("#selection-slots"),
    compareBoard: document.querySelector("#compare-board"),
    toast: document.querySelector("#toast")
  };

  let toastTimer;
  const descriptionRequests = new Map();

  const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

  if (catalogMode === "inline") {
    elements.catalogStatus.textContent = "Live from the selected Shopify collections and product metafields.";
    elements.catalogStatus.classList.add("is-live");
  } else if (catalogMode === "live") {
    elements.catalogStatus.textContent = failedCollections.length
      ? `Live from Shopify. Could not load: ${failedCollections.join(", ")}.`
      : "Live from the selected Shopify collections.";
    elements.catalogStatus.classList.add(failedCollections.length ? "is-warning" : "is-live");
  } else {
    elements.catalogStatus.textContent = window.location.protocol === "file:"
      ? "Local preview data. Shopify will load the selected collections live after installation."
      : "Using the local fallback catalog because Shopify could not be reached.";
    elements.catalogStatus.classList.add("is-warning");
  }

  function optimizedImage(url, width) {
    try {
      const imageUrl = new URL(url);
      imageUrl.searchParams.set("width", String(width));
      return imageUrl.toString();
    } catch {
      return url;
    }
  }

  function decodeHtml(value) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  }

  function extractProductInfoFromHtml(html) {
    const documentNode = new DOMParser().parseFromString(html, "text/html");
    const headings = [...documentNode.querySelectorAll("button, h2, h3, h4")];
    const heading = headings.find((node) => /ingredients\s*(and|&)\s*(particle|micron)|ingredients\s*2/i.test(node.textContent || ""));

    if (heading) {
      const panelId = heading.getAttribute("aria-controls");
      const panel = panelId ? documentNode.getElementById(panelId) : heading.parentElement?.nextElementSibling;
      const metafield = panel?.querySelector(".metafield-multi_line_text_field, .metafield-rich_text_field") || panel;
      const text = metafield?.textContent?.replace(/\u00a0/g, " ").replace(/\n\s*\n\s*\n+/g, "\n\n").trim();
      if (text) return text;
    }

    const metafieldValues = [...documentNode.querySelectorAll(".metafield-multi_line_text_field")]
      .map((node) => node.textContent?.replace(/\u00a0/g, " ").trim())
      .filter(Boolean);
    return metafieldValues.find((text) => /particle size|micron|ingredients/i.test(text)) || "";
  }

  async function loadProductDescription(product) {
    if (product.description) return product.description;
    if (descriptionRequests.has(product.handle)) return descriptionRequests.get(product.handle);

    const request = fetch(product.url, { headers: { Accept: "text/html" } })
      .then((response) => response.ok ? response.text() : "")
      .then((html) => extractProductInfoFromHtml(decodeHtml(html)))
      .catch(() => "");

    descriptionRequests.set(product.handle, request);
    const description = await request;
    product.description = description;
    return description;
  }

  function showToast(message) {
    window.clearTimeout(toastTimer);
    elements.toast.textContent = message;
    elements.toast.classList.add("is-visible");
    toastTimer = window.setTimeout(() => elements.toast.classList.remove("is-visible"), 2400);
  }

  function updateUrl() {
    const next = new URLSearchParams();
    if (state.query) next.set("q", state.query);
    if (state.collection !== "all") next.set("collection", state.collection);
    if (state.sort !== "featured") next.set("sort", state.sort);
    if (state.selected.length) next.set("compare", state.selected.map((product) => product.handle).join(","));
    const query = next.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
  }

  function getFilteredProducts() {
    const query = state.query.trim().toLowerCase();
    const filtered = catalog.products.filter((product) => {
      const matchesCollection = state.collection === "all" || product.collectionHandles.includes(state.collection);
      const searchText = [product.title, ...product.collectionLabels, ...(product.tags || [])].join(" ").toLowerCase();
      return matchesCollection && (!query || searchText.includes(query));
    });

    return filtered.sort((a, b) => {
      if (state.sort === "name-asc") return a.title.localeCompare(b.title);
      if (state.sort === "price-asc") return a.price - b.price;
      if (state.sort === "price-desc") return b.price - a.price;
      return a.rank - b.rank;
    });
  }

  function productIsSelected(product) {
    return state.selected.some((selected) => selected.id === product.id);
  }

  function createIconLink(product) {
    const link = document.createElement("a");
    link.className = "icon-link";
    link.href = product.url;
    link.target = "_blank";
    link.rel = "noreferrer";
    link.setAttribute("aria-label", `Open ${product.title} product page`);
    link.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M14 5h5v5M10 14 19 5M19 14v5H5V5h5"/></svg>';
    return link;
  }

  function createProductCard(product) {
    const selected = productIsSelected(product);
    const card = document.createElement("article");
    card.className = `product-card${selected ? " is-selected" : ""}`;
    card.dataset.productId = product.id;

    const imageWrap = document.createElement("div");
    imageWrap.className = "product-card__image-wrap";

    const image = document.createElement("img");
    image.className = "product-card__image";
    image.src = optimizedImage(product.image, 520);
    image.alt = product.imageAlt || product.title;
    image.loading = "lazy";
    image.decoding = "async";
    image.addEventListener("error", () => {
      image.alt = `${product.title} image unavailable`;
      imageWrap.style.background = "#ecece8";
    }, { once: true });
    imageWrap.append(image);

    if (product.collectionLabels.length) {
      const badge = document.createElement("span");
      badge.className = "product-card__badge";
      badge.textContent = product.collectionLabels.slice(0, 2).join(" + ");
      imageWrap.append(badge);
    }

    const body = document.createElement("div");
    body.className = "product-card__body";

    const title = document.createElement("h3");
    title.className = "product-card__title";
    title.textContent = product.title;

    const price = document.createElement("p");
    price.className = "product-card__price";
    price.textContent = `${product.available ? "From " : "Currently unavailable | "}${money.format(product.price)}`;

    const actions = document.createElement("div");
    actions.className = "product-card__actions";

    const compareButton = document.createElement("button");
    compareButton.className = "compare-button";
    compareButton.type = "button";
    compareButton.setAttribute("aria-pressed", String(selected));
    compareButton.textContent = selected ? "Selected" : "Add to compare";
    compareButton.addEventListener("click", () => toggleProduct(product));

    actions.append(compareButton, createIconLink(product));
    body.append(title, price, actions);
    card.append(imageWrap, body);
    return card;
  }

  function renderFilters() {
    elements.filters.replaceChildren();
    const productCounts = new Map();
    catalog.collections.forEach((collection) => {
      productCounts.set(collection.Handle, catalog.products.filter((product) => product.collectionHandles.includes(collection.Handle)).length);
    });

    const filters = [{ Handle: "all", Label: "All products" }, ...catalog.collections];
    filters.forEach((collection) => {
      const button = document.createElement("button");
      button.className = "filter-chip";
      button.type = "button";
      button.setAttribute("aria-pressed", String(state.collection === collection.Handle));
      button.append(document.createTextNode(collection.Label));

      const count = document.createElement("small");
      count.textContent = collection.Handle === "all" ? catalog.products.length : productCounts.get(collection.Handle);
      button.append(count);
      button.addEventListener("click", () => {
        state.collection = collection.Handle;
        state.limit = 36;
        render();
      });
      elements.filters.append(button);
    });
  }

  function renderProducts() {
    const products = getFilteredProducts();
    const visible = products.slice(0, state.limit);
    elements.grid.replaceChildren(...visible.map(createProductCard));
    elements.count.textContent = `${products.length} product${products.length === 1 ? "" : "s"}`;
    elements.loadMore.hidden = visible.length >= products.length;
  }

  function createSelectionSlot(product, index) {
    const slot = document.createElement("div");
    slot.className = `selection-slot${product ? " has-product" : ""}`;
    if (!product) {
      slot.textContent = `Product ${index + 1}`;
      return slot;
    }

    const image = document.createElement("img");
    image.src = optimizedImage(product.image, 120);
    image.alt = "";

    const name = document.createElement("span");
    name.textContent = product.title;

    const remove = document.createElement("button");
    remove.type = "button";
    remove.setAttribute("aria-label", `Remove ${product.title}`);
    remove.textContent = "x";
    remove.addEventListener("click", () => toggleProduct(product));

    slot.append(image, name, remove);
    return slot;
  }

  function createCompareDetail(product, index) {
    const detail = document.createElement("article");
    detail.className = "compare-product";

    const gallery = document.createElement("div");
    gallery.className = "compare-gallery";

    const imageWrap = document.createElement("div");
    imageWrap.className = "compare-product__image-wrap";

    const image = document.createElement("img");
    image.className = "compare-product__image";
    image.loading = "eager";
    imageWrap.append(image);

    const images = normalizeImages(product);
    let activeImageIndex = 0;

    const previous = document.createElement("button");
    previous.className = "compare-gallery__arrow compare-gallery__arrow--previous";
    previous.type = "button";
    previous.setAttribute("aria-label", `Show previous image for ${product.title}`);
    previous.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m15 18-6-6 6-6"/></svg>';

    const next = document.createElement("button");
    next.className = "compare-gallery__arrow compare-gallery__arrow--next";
    next.type = "button";
    next.setAttribute("aria-label", `Show next image for ${product.title}`);
    next.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"/></svg>';

    const counter = document.createElement("span");
    counter.className = "compare-gallery__counter";

    const thumbnails = document.createElement("div");
    thumbnails.className = "compare-gallery__thumbnails";
    thumbnails.setAttribute("aria-label", `${product.title} product images`);

    const thumbnailButtons = images.map((galleryImage, galleryIndex) => {
      const button = document.createElement("button");
      button.className = "compare-gallery__thumbnail";
      button.type = "button";
      button.setAttribute("aria-label", `Show image ${galleryIndex + 1} of ${images.length} for ${product.title}`);

      const thumbnail = document.createElement("img");
      thumbnail.src = optimizedImage(galleryImage.src, 110);
      thumbnail.alt = "";
      thumbnail.loading = "lazy";
      button.append(thumbnail);
      button.addEventListener("click", () => setActiveImage(galleryIndex));
      thumbnails.append(button);
      return button;
    });

    function setActiveImage(nextIndex) {
      activeImageIndex = (nextIndex + images.length) % images.length;
      const activeImage = images[activeImageIndex];
      image.src = optimizedImage(activeImage.src, 720);
      image.alt = activeImage.alt;
      counter.textContent = `${activeImageIndex + 1} / ${images.length}`;
      thumbnailButtons.forEach((button, buttonIndex) => {
        button.setAttribute("aria-current", buttonIndex === activeImageIndex ? "true" : "false");
      });
      thumbnailButtons[activeImageIndex]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }

    previous.addEventListener("click", () => setActiveImage(activeImageIndex - 1));
    next.addEventListener("click", () => setActiveImage(activeImageIndex + 1));

    if (images.length > 1) imageWrap.append(previous, next, counter);
    setActiveImage(0);
    gallery.append(imageWrap);
    if (images.length > 1) gallery.append(thumbnails);

    const label = document.createElement("p");
    label.className = "compare-product__label";
    label.textContent = `Product ${index + 1}`;

    const title = document.createElement("h3");
    title.textContent = product.title;

    const meta = document.createElement("p");
    meta.className = "compare-product__meta";
    meta.textContent = `${product.collectionLabels.join(" | ")} | From ${money.format(product.price)}`;

    const description = document.createElement("div");
    description.className = "compare-product__description";

    const descriptionLabel = document.createElement("p");
    descriptionLabel.className = "compare-product__description-label";
    descriptionLabel.textContent = collectionConfig.descriptionHeading || "Ingredients and particle size";

    const descriptionText = document.createElement("p");
    descriptionText.textContent = product.description || "Loading product information...";
    description.append(descriptionLabel, descriptionText);

    if (!product.description) {
      loadProductDescription(product).then((loadedDescription) => {
        descriptionText.textContent = loadedDescription || "Product information is not available for this item.";
      });
    }

    const link = document.createElement("a");
    link.className = "compare-product__link";
    link.href = product.url;
    link.target = "_blank";
    link.rel = "noreferrer";
    link.textContent = "View product";

    const remove = document.createElement("button");
    remove.className = "compare-product__remove";
    remove.type = "button";
    remove.setAttribute("aria-label", `Remove ${product.title} from comparison`);
    remove.textContent = "x";
    remove.addEventListener("click", () => toggleProduct(product));

    const actions = document.createElement("div");
    actions.className = "compare-product__actions";
    actions.append(link, remove);

    const purchase = document.createElement("div");
    purchase.className = "compare-product__purchase";

    const variants = normalizeVariants(product);
    const availableVariants = variants.filter((variant) => variant.available);
    let selectedVariantId = availableVariants[0]?.id || variants[0]?.id || "";

    if (variants.length > 1 || (variants[0] && variants[0].title !== "Default Title")) {
      const variantLabel = document.createElement("label");
      variantLabel.className = "compare-product__variant-label";
      variantLabel.textContent = "Choose option";

      const variantSelect = document.createElement("select");
      variantSelect.className = "compare-product__variant-select";
      variantSelect.setAttribute("aria-label", `Choose an option for ${product.title}`);

      variants.forEach((variant) => {
        const option = document.createElement("option");
        option.value = variant.id;
        option.disabled = !variant.available;
        option.textContent = `${variant.title} - ${money.format(variant.price)}${variant.available ? "" : " - Sold out"}`;
        if (variant.id === selectedVariantId) option.selected = true;
        variantSelect.append(option);
      });
      variantSelect.addEventListener("change", () => {
        selectedVariantId = variantSelect.value;
      });
      variantLabel.append(variantSelect);
      purchase.append(variantLabel);
    }

    const addToCart = document.createElement("button");
    addToCart.className = "compare-product__add-to-cart";
    addToCart.type = "button";
    addToCart.disabled = !availableVariants.length;
    addToCart.textContent = availableVariants.length ? "Add to cart" : "Sold out";
    addToCart.addEventListener("click", async () => {
      if (!selectedVariantId || addToCart.disabled) return;
      const originalLabel = addToCart.textContent;
      addToCart.disabled = true;
      addToCart.textContent = "Adding...";

      try {
        const routeRoot = window.Shopify?.routes?.root || "/";
        const response = await fetch(`${routeRoot}cart/add.js`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ items: [{ id: Number(selectedVariantId), quantity: 1 }] })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || "This item could not be added.");

        addToCart.textContent = "Added to cart";
        document.dispatchEvent(new CustomEvent("eurus:cart:reload"));
        document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));
        document.dispatchEvent(new CustomEvent("eurus:product:added", { detail: { product: result } }));
        window.setTimeout(() => {
          if (window.Alpine?.store("xMiniCart")) window.Alpine.store("xMiniCart").openCart();
        }, 350);
        window.setTimeout(() => {
          addToCart.disabled = false;
          addToCart.textContent = originalLabel;
        }, 2200);
      } catch (error) {
        addToCart.textContent = error.message || "Unable to add";
        window.setTimeout(() => {
          addToCart.disabled = false;
          addToCart.textContent = originalLabel;
        }, 2800);
      }
    });
    purchase.append(addToCart);

    detail.append(gallery, label, title, meta, description, actions, purchase);
    return detail;
  }

  function renderSelection() {
    elements.selectionCount.textContent = `${state.selected.length} of ${state.maxSelected} selected`;
    const slots = state.selected.map((product, index) => createSelectionSlot(product, index));
    if (state.selected.length === 0) slots.push(createSelectionSlot(null, 0));
    elements.selectionSlots.replaceChildren(...slots);
    elements.compareNow.disabled = state.selected.length < 2;

    const ready = state.selected.length >= 2;
    elements.comparison.hidden = !ready;
    if (!ready) return;
    elements.compareBoard.replaceChildren(...state.selected.map(createCompareDetail));
  }

  function toggleProduct(product) {
    const selectedIndex = state.selected.findIndex((selected) => selected.id === product.id);
    if (selectedIndex >= 0) {
      state.selected.splice(selectedIndex, 1);
    } else if (state.selected.length < state.maxSelected) {
      state.selected.push(product);
      if (state.selected.length === 2) showToast(`Your comparison is ready. You can add up to ${state.maxSelected - 2} more products.`);
      if (state.selected.length === state.maxSelected) showToast(`${state.maxSelected} products selected. The comparison is full.`);
    } else {
      showToast(`You can compare up to ${state.maxSelected} products. Remove one before adding another.`);
      return;
    }
    renderProducts();
    renderSelection();
    writeStoredHandles(state.selected);
    updateUrl();
  }

  function render() {
    renderFilters();
    renderProducts();
    renderSelection();
    updateUrl();
  }

  elements.search.value = state.query;
  elements.sort.value = state.sort;

  elements.search.addEventListener("input", (event) => {
    state.query = event.target.value;
    state.limit = 36;
    render();
  });

  elements.sort.addEventListener("change", (event) => {
    state.sort = event.target.value;
    render();
  });

  elements.clearFilters.addEventListener("click", () => {
    state.query = "";
    state.collection = "all";
    state.sort = "featured";
    state.limit = 36;
    elements.search.value = "";
    elements.sort.value = "featured";
    render();
  });

  elements.loadMore.addEventListener("click", () => {
    state.limit += 36;
    renderProducts();
  });

  elements.clearComparison.addEventListener("click", () => {
    state.selected = [];
    renderProducts();
    renderSelection();
    writeStoredHandles(state.selected);
    updateUrl();
  });

  elements.compareNow.addEventListener("click", () => {
    elements.comparison.scrollIntoView({ behavior: "smooth", block: "start" });
  });

  render();
  writeStoredHandles(state.selected);

  if (window.location.hash === "#comparison" && state.selected.length >= 2) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        elements.comparison.scrollIntoView({ behavior: "auto", block: "start" });
      });
    });
  }
})();
